import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/I18nContext';
import { Shield, Key, AlertTriangle, Globe, Apple } from 'lucide-react';
import { toast } from 'sonner';
import { MfaEnableModal } from './MfaEnableModal';
import { PasswordChangeForm } from './PasswordChangeForm';
import { SocialLoginInfo } from './SocialLoginInfo';
import type { User } from '@/services/interfaces/Auth';

interface SecuritySectionProps {
  user: User;
}

export const SecuritySection: React.FC<SecuritySectionProps> = ({ user }) => {
  const { t } = useTranslation();
  const { updateUserProfile } = useAuth();
  const [mfaModalVisible, setMfaModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 获取社交登录提供者信息
  const getSocialProvider = (user: User) => {
    if (!user.auth0Id) return null;
    
    const subParts = user.auth0Id.split('|');
    if (subParts.length < 2) return null;
    
    const providerKey = subParts[0];
    
    const providers: Record<string, { name: string; icon: React.ReactNode; color: string }> = {
      'google-oauth2': {
        name: 'Google',
        icon: <Globe className="h-4 w-4" />,
        color: '#4285F4'
      },
      'apple': { 
        name: 'Apple', 
        icon: <Apple className="h-4 w-4" />, 
        color: '#000000' 
      },
    };
    
    return providers[providerKey] || null;
  };

  const socialProvider = getSocialProvider(user);
  const isSocialLogin = !!socialProvider;

  // 处理MFA切换
  const handleMfaToggle = async (enable: boolean) => {
    if (isSocialLogin && enable) {
      setMfaModalVisible(true);
      return;
    }

    setIsLoading(true);
    try {
      // TODO: 调用API切换MFA状态
      // await API.toggleMfa(enable);
      updateUserProfile({ ...user, mfaEnabled: enable });
      toast.success(enable ? t('security.mfaEnabled') : t('security.mfaDisabled'));
      setMfaModalVisible(false);
    } catch (error) {
      toast.error(t('security.mfaUpdateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          {t('settings.security')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* MFA 设置 */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium">{t('security.twoFactorAuth')}</h3>
                {user.mfaEnabled && (
                  <Badge variant="secondary" className="text-xs">
                    {t('security.enabled')}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {t('security.mfaDescription')}
              </p>
            </div>
            <Button
              className='text-white'
              variant={user.mfaEnabled ? "destructive" : "default"}
              size="sm"
              onClick={() => handleMfaToggle(!user.mfaEnabled)}
              disabled={isLoading}
            >
              {user.mfaEnabled ? t('security.disable') : t('security.enable')}
            </Button>
          </div>

          {isSocialLogin && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">{t('security.socialLoginMfaWarning')}</p>
                <p className="text-xs mt-1">{t('security.socialLoginMfaWarningDesc')}</p>
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* 登录方式信息 */}
        {isSocialLogin ? (
          <SocialLoginInfo provider={socialProvider!} />
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              <h3 className="font-medium">{t('security.password')}</h3>
            </div>
            <PasswordChangeForm />
          </div>
        )}

        {/* 登录历史 */}
        <Separator />
        <div className="space-y-3">
          <h3 className="font-medium">{t('security.loginHistory')}</h3>
          <div className="text-sm text-muted-foreground">
            <p>{t('security.lastLogin')}: {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : t('security.never')}</p>
            <p className="mt-1">{t('security.accountCreated')}: {new Date(user.created_at).toLocaleString()}</p>
          </div>
        </div>
      </CardContent>

      {/* MFA 启用模态框 */}
      <MfaEnableModal
        visible={mfaModalVisible}
        onOk={() => handleMfaToggle(true)}
        onCancel={() => setMfaModalVisible(false)}
        isSocialLogin={isSocialLogin}
      />
    </Card>
  );
};
